import { useCallback, useEffect, useRef, useState } from 'react';
import { api, fetchAutenticado, ErrorApi } from '../api/cliente';
import { Cargando, Dialogo, Error as AvisoError } from '../componentes/comunes';
import { useSesion } from '../sesion';

/**
 * Integrar UUIDs.
 *
 * Es el paso que convierte una prefactura en factura. APRISA deja el TXT
 * en la carpeta que lee Master EDI, Master EDI timbra, y devuelve otro TXT
 * con lo que el SAT selló. Hasta que ese archivo se sube aquí, todo lo
 * emitido sigue siendo prefactura.
 *
 * El archivo se sube dos veces a propósito: primero para ver qué pasaría y
 * luego para aplicarlo. Integrar un UUID equivocado no se deshace con un
 * botón —se deshace con una cancelación fiscal—, así que la pantalla no
 * ofrece aplicar nada que no se haya visto antes.
 */

const NOMBRE_RESULTADO: Record<string, string> = {
  integrar: 'Se integra',
  cancelada: 'Se integra (cancelada)',
  ya_estaba: 'Ya estaba',
  sin_uuid: 'Sin UUID',
  uuid_invalido: 'UUID mal formado',
  no_encontrada: 'No se encontró',
  otro_emisor: 'Otro emisor',
  otro_receptor: 'Otro receptor',
  importe_distinto: 'Importe distinto',
  ya_timbrada_con_otro: 'Ya tenía otro UUID',
  uuid_de_otra: 'UUID de otra factura',
};

/** Los que se van a escribir. El resto queda anotado y sin tocar. */
const SE_APLICAN = ['integrar', 'cancelada'];

interface Renglon {
  renglon: number;
  resultado: string;
  mensaje: string;
  serie: string;
  folio: string;
  uuid: string;
  folioInterno: string | null;
}

interface Resumen {
  total: number;
  integradas: number;
  sinCambio: number;
  conDiscrepancia: number;
}

interface Respuesta {
  resumen: Resumen;
  renglones: Renglon[];
}

interface Importacion {
  id: string;
  nombreArchivo: string;
  totalLineas: number;
  integradas: number;
  conDiscrepancia: number;
  creadoEn: string;
}

function fechaHora(iso: string): string {
  return new Date(iso).toLocaleString('es-MX', {
    day: '2-digit', month: 'short', year: 'numeric', hour: '2-digit', minute: '2-digit',
  });
}

/** Sube el archivo. Va con fetch porque es multipart y no JSON. */
async function subir(archivo: File, aplicar: boolean): Promise<Respuesta> {
  const cuerpo = new FormData();
  cuerpo.append('archivo', archivo);

  const respuesta = await fetchAutenticado(
    aplicar ? '/api/v1/facturacion/uuids' : '/api/v1/facturacion/uuids/vista-previa',
    { method: 'POST', body: cuerpo },
  );

  const datos = await respuesta.json().catch(() => ({}));

  if (!respuesta.ok) {
    throw new ErrorApi(
      respuesta.status,
      String(datos.codigo_negocio ?? 'error'),
      String(datos.detail ?? 'No se pudo leer el archivo.'),
    );
  }

  return datos as Respuesta;
}

export default function IntegrarUuids() {
  const { puede } = useSesion();
  const entrada = useRef<HTMLInputElement>(null);
  const [archivo, setArchivo] = useState<File | null>(null);
  const [previa, setPrevia] = useState<Respuesta | null>(null);
  const [aplicado, setAplicado] = useState<Resumen | null>(null);
  const [trabajando, setTrabajando] = useState(false);
  const [error, setError] = useState<unknown>(null);
  const [historial, setHistorial] = useState<Importacion[]>([]);
  /** El archivo del histórico que se está mirando. */
  const [viendo, setViendo] = useState<Importacion | null>(null);

  const cargarHistorial = useCallback(() => {
    api.get<Importacion[]>('/facturacion/uuids/historial')
      .then(setHistorial)
      .catch(() => undefined);
  }, []);

  useEffect(cargarHistorial, [cargarHistorial]);

  function elegir(nuevo: File | null) {
    setArchivo(nuevo);
    setPrevia(null);
    setAplicado(null);
    setError(null);
    if (!nuevo) return;

    setTrabajando(true);
    subir(nuevo, false)
      .then(setPrevia)
      .catch(setError)
      .finally(() => setTrabajando(false));
  }

  function aplicar() {
    if (!archivo) return;

    setTrabajando(true);
    setError(null);
    subir(archivo, true)
      .then((r) => {
        setAplicado(r.resumen);
        setPrevia(r);
        cargarHistorial();
      })
      .catch(setError)
      .finally(() => setTrabajando(false));
  }

  const hayQueAplicar = (previa?.resumen.integradas ?? 0) > 0;

  return (
    <>
      <div className="panel">
        <h2>Importar el archivo del timbrado</h2>
        <p className="sub">
          El TXT que devuelve Master EDI con lo que el SAT selló. De cada
          renglón se toma el UUID y se pega a su prefactura por serie y
          folio. Primero se ve qué pasaría; nada se escribe hasta que lo
          apruebes.
        </p>

        <div
          className="aviso"
          role="button"
          tabIndex={0}
          style={{ textAlign: 'center', cursor: 'pointer', padding: 24, marginTop: 12 }}
          onClick={() => entrada.current?.click()}
          onKeyDown={(e) => { if (e.key === 'Enter') entrada.current?.click(); }}
          onDragOver={(e) => e.preventDefault()}
          onDrop={(e) => {
            e.preventDefault();
            elegir(e.dataTransfer.files[0] ?? null);
          }}
        >
          {archivo
            ? <strong>{archivo.name}</strong>
            : <>Arrastra aquí el TXT del timbrado, o haz clic para elegirlo.</>}
          <div className="conteo" style={{ marginTop: 6 }}>
            Un renglón por comprobante, con los campos separados por barra
            vertical: RFC emisor, RFC receptor, serie, folio, monto, fecha,
            estatus, UUID, número de sucursal y de ticket.
          </div>
        </div>

        <input
          ref={entrada} type="file" accept=".txt,text/plain" style={{ display: 'none' }}
          onChange={(e) => elegir(e.target.files?.[0] ?? null)}
        />

        <AvisoError error={error} />

        {trabajando && <p className="sub">Leyendo el archivo…</p>}

        {previa && (
          <>
            <div className="rejilla-catalogos" style={{ marginTop: 16 }}>
              <Conteo titulo="Renglones leídos" valor={previa.resumen.total} />
              <Conteo
                titulo={aplicado ? 'Integradas' : 'Se van a integrar'}
                valor={previa.resumen.integradas}
              />
              <Conteo titulo="Sin cambio" valor={previa.resumen.sinCambio} />
              <Conteo
                titulo="Con discrepancia"
                valor={previa.resumen.conDiscrepancia}
                alerta={previa.resumen.conDiscrepancia > 0}
              />
            </div>

            {aplicado ? (
              <div className="aviso exito" style={{ marginTop: 14 }}>
                Listo: {aplicado.integradas} prefacturas quedaron timbradas.
                {aplicado.conDiscrepancia > 0 && (
                  <> Quedan {aplicado.conDiscrepancia} renglones sin aplicar,
                  con su motivo abajo. Ninguno se tocó.</>
                )}
              </div>
            ) : (
              <div className="controles" style={{ marginTop: 14 }}>
                {puede('facturacion', 'editar') && (
                  <button className="primario" onClick={aplicar} disabled={trabajando || !hayQueAplicar}>
                    Integrar {previa.resumen.integradas} UUID
                  </button>
                )}
                <button onClick={() => elegir(null)} disabled={trabajando}>Descartar</button>
                {!hayQueAplicar && (
                  <span className="conteo">
                    No hay nada que integrar en este archivo.
                  </span>
                )}
              </div>
            )}

            <table style={{ marginTop: 16 }}>
              <thead>
                <tr>
                  <th className="numero">Renglón</th>
                  <th>Comprobante</th>
                  <th>UUID</th>
                  <th>Resultado</th>
                  <th>Qué pasó</th>
                </tr>
              </thead>
              <tbody>
                {previa.renglones.map((r) => (
                  <tr key={r.renglon}>
                    <td className="numero">{r.renglon}</td>
                    <td className="mono">
                      {r.serie}-{r.folio}
                      {r.folioInterno && r.folioInterno !== `${r.serie}-${r.folio}` && (
                        <div className="conteo">{r.folioInterno}</div>
                      )}
                    </td>
                    <td className="mono" style={{ fontSize: 11 }}>{r.uuid || '—'}</td>
                    <td>
                      <span className={SE_APLICAN.includes(r.resultado)
                        ? 'etiqueta si'
                        : r.resultado === 'ya_estaba' ? undefined : 'etiqueta no'}
                      >
                        {NOMBRE_RESULTADO[r.resultado] ?? r.resultado}
                      </span>
                    </td>
                    <td className="conteo">{r.mensaje}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </>
        )}
      </div>

      {historial.length > 0 && (
        <div className="panel">
          <h2>Archivos importados</h2>
          <p className="sub">
            Cuando una factura aparece con el UUID que no era, lo primero
            que se pregunta es de qué archivo salió.
          </p>
          <table>
            <thead>
              <tr>
                <th>Archivo</th>
                <th>Cuándo</th>
                <th className="numero">Renglones</th>
                <th className="numero">Integradas</th>
                <th className="numero">Con discrepancia</th>
              </tr>
            </thead>
            <tbody>
              {historial.map((i) => (
                <tr key={i.id}>
                  {/* El nombre abre lo que traía el archivo.

                      Karin: «en integrar uuids - archivos importados -
                      poder ver el archivo cargado, como un preview». El
                      dato ya se guardaba —`importacion_uuid.detalle`
                      conserva el renglón por renglón conciliado— y no se
                      podía mirar: el histórico decía cuántos y no cuáles.

                      Se enseña lo conciliado y no el texto crudo del TXT:
                      es la misma información más lo que el sistema
                      entendió de ella, que es lo que hace falta cuando una
                      factura aparece con el UUID que no era. */}
                  <td className="mono">
                    <button className="enlace" onClick={() => setViendo(i)}>
                      {i.nombreArchivo}
                    </button>
                  </td>
                  <td>{fechaHora(i.creadoEn)}</td>
                  <td className="numero">{i.totalLineas}</td>
                  <td className="numero">{i.integradas}</td>
                  <td className="numero">
                    {i.conDiscrepancia > 0
                      ? <span className="etiqueta no">{i.conDiscrepancia}</span>
                      : i.conDiscrepancia}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {viendo && (
        <DetalleDelArchivo importacion={viendo} onCerrar={() => setViendo(null)} />
      )}
    </>
  );
}

/**
 * Lo que traía un archivo ya importado, renglón por renglón.
 *
 * Karin: «poder ver el archivo cargado, como un preview». Se lee de
 * `importacion_uuid.detalle`, que guarda la conciliación del día que se
 * subió: no se vuelve a conciliar contra el estado de hoy. Es a propósito
 * —lo que se quiere saber es qué decía ese archivo y qué se hizo con él,
 * no qué pasaría si se subiera otra vez—.
 *
 * Misma tabla que la vista previa, con las mismas etiquetas: es la misma
 * información y dos dibujos distintos obligarían a aprenderla dos veces.
 */
function DetalleDelArchivo({ importacion, onCerrar }: {
  importacion: Importacion;
  onCerrar: () => void;
}) {
  const [renglones, setRenglones] = useState<Renglon[] | null>(null);
  const [error, setError] = useState<unknown>(null);

  useEffect(() => {
    // `historial/:id` y no `:id`: el detalle cuelga del historial, que es
    // de donde se abre. Lo tenía mal y la API contestaba «Cannot GET».
    api.get<{ renglones: Renglon[] }>(`/facturacion/uuids/historial/${importacion.id}`)
      .then((r) => setRenglones(r.renglones))
      .catch(setError);
  }, [importacion.id]);

  return (
    <Dialogo
      titulo={importacion.nombreArchivo}
      ancho
      onCerrar={onCerrar}
      pie={<button onClick={onCerrar}>Cerrar</button>}
    >
      <AvisoError error={error} />

      <p className="sub" style={{ marginTop: 0 }}>
        Importado el {fechaHora(importacion.creadoEn)} · {importacion.totalLineas} renglón(es)
        {' · '}{importacion.integradas} integrada(s)
        {importacion.conDiscrepancia > 0 && ` · ${importacion.conDiscrepancia} con discrepancia`}
      </p>

      {!renglones ? <Cargando /> : renglones.length === 0 ? (
        <div className="vacio">
          De este archivo no se guardó el detalle. Se conserva desde que la
          importación empezó a registrarlo; los anteriores sólo tienen sus
          conteos.
        </div>
      ) : (
        <table>
          <thead>
            <tr>
              <th className="numero">Renglón</th>
              <th>Comprobante</th>
              <th>UUID</th>
              <th>Resultado</th>
              <th>Qué pasó</th>
            </tr>
          </thead>
          <tbody>
            {renglones.map((r) => (
              <tr key={r.renglon}>
                <td className="numero">{r.renglon}</td>
                <td className="mono">
                  {r.serie}-{r.folio}
                  {r.folioInterno && r.folioInterno !== `${r.serie}-${r.folio}` && (
                    <div className="conteo">{r.folioInterno}</div>
                  )}
                </td>
                <td className="mono" style={{ fontSize: 11 }}>{r.uuid || '—'}</td>
                <td>
                  <span className={SE_APLICAN.includes(r.resultado)
                    ? 'etiqueta si'
                    : r.resultado === 'ya_estaba' ? undefined : 'etiqueta no'}
                  >
                    {NOMBRE_RESULTADO[r.resultado] ?? r.resultado}
                  </span>
                </td>
                <td className="conteo">{r.mensaje}</td>
              </tr>
            ))}
          </tbody>
        </table>
      )}
    </Dialogo>
  );
}

function Conteo({ titulo, valor, alerta }: { titulo: string; valor: number; alerta?: boolean }) {
  return (
    <div className="tarjeta" style={{ cursor: 'default' }}>
      <div className="conteo">{titulo}</div>
      <div style={{ fontSize: 22, fontWeight: 700, color: alerta ? '#b02a37' : undefined }}>
        {valor}
      </div>
    </div>
  );
}
